import { createTRPCClient, httpBatchLink } from '@trpc/client';
import {
  createTRPCContext,
  createTRPCOptionsProxy,
} from '@trpc/tanstack-react-query';
import superjson from 'superjson';
import type { QueryClient } from '@tanstack/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@minty/server/router';

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();

/** `RouterOutputs['tasks']['counters']['countTasks']` and friends. */
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * Same-origin in every environment: the clinic server serves the SPA, and in
 * development Vite proxies `/trpc`. The session is a cookie, so nothing else
 * has to be attached.
 */
export function createClient() {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        url: '/trpc',
        transformer: superjson,
        fetch: (url, options) =>
          fetch(url, { ...options, credentials: 'same-origin' }),
      }),
    ],
  });
}

export type TrpcClient = ReturnType<typeof createClient>;

/** For loaders, which run outside React and so cannot call `useTRPC()`. */
export function createOptionsProxy(
  client: TrpcClient,
  queryClient: QueryClient,
) {
  return createTRPCOptionsProxy<AppRouter>({ client, queryClient });
}

export type TrpcOptions = ReturnType<typeof createOptionsProxy>;
